<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    import { api } from "../lib/api";
    import { account } from "../lib/stores";
    import { activities } from "../lib/constants";
    import { toast } from "../lib/toast";
    import { deviceStats } from "../lib/stats";

    import Button from "../lib/components/Button.svelte";
    import Input from "../lib/components/Input.svelte";
    import Label from "../lib/components/Label.svelte";
    import Checkbox from "../lib/components/Checkbox.svelte";
    import Badge from "../lib/components/Badge.svelte";
    import Tooltip from "../lib/components/Tooltip.svelte";
    import Dialog from "../lib/components/Dialog.svelte";
    import Dropdown from "../lib/components/Dropdown.svelte";
    import DropdownItem from "../lib/components/DropdownItem.svelte";
    import Battery from "./utility/Battery.svelte";
    import Network from "./utility/Network.svelte";

    let latestAppVersion = "";
    let latestAppURL = "";
    let devices: any[] = [];
    let logs: any[] = [];

    // Modals
    let createOpen = false;
    let editOpen = false;
    let logsOpen = false;
    let connsOpen = false;
    let liveConns: any[] = [];
    let connHistory: any[] = [];
    let connsLoading = false;
    let current: any = null;

    let isShowOnlyOnline =
        localStorage.getItem("isShowOnlyOnline") === "true";
    let search = localStorage.getItem("search") || "";

    function formatBytes(n: number): string {
        if (!n || n <= 0) return "0";
        const units = ["B", "KB", "MB", "GB", "TB"];
        let i = 0;
        let v = n;
        while (v >= 1024 && i < units.length - 1) {
            v /= 1024;
            i++;
        }
        return v.toFixed(v < 10 ? 2 : v < 100 ? 1 : 0) + " " + units[i];
    }

    // До фикса сервер писал в лог скорость в байтах/с (ByteRate из speedtest-go),
    // а не в мегабитах — такие записи уже лежат в БД. Ни один мобильный канал не
    // даёт 10 000 Mbit, поэтому всё, что выше порога, считаем байтами/с.
    const LEGACY_BYTE_RATE_MIN = 10000;

    function toMbit(value: any): number | null {
        const n = typeof value === "number" ? value : parseFloat(value);
        if (!isFinite(n) || n <= 0) return null;
        return n > LEGACY_BYTE_RATE_MIN ? n / 125000 : n;
    }

    function formatSpeed(value: any): string {
        const mbit = toMbit(value);
        return mbit === null ? "-" : mbit.toFixed(2) + " Mbit";
    }

    async function update() {
        const response = await api.getDevices(
            "withExternalIp=1&withSpeedtest=1&withTraffic=1",
        );
        if (response.error) return;

        if (response.data) {
            // Total/online count рассчитываем ДО клиентских фильтров.
            const total = response.data.length;
            const online = response.data.filter(
                (x: any) => x.status === "online",
            ).length;
            deviceStats.set({ online, total });

            response.data = response.data.filter((x: any) => {
                const matchesSearch =
                    search.length > 0
                        ? x.name.toLowerCase().includes(search.toLowerCase())
                        : true;
                const matchesOnline = isShowOnlyOnline
                    ? x.status === "online"
                    : true;
                return matchesSearch && matchesOnline;
            });
            response.data.sort((a: any, b: any) =>
                a.name.localeCompare(b.name),
            );
            devices = response.data;
        }
    }

    async function updateRelease() {
        try {
            const response = await fetch("/release.json?t=" + +new Date());
            const data = await response.json();
            latestAppVersion = data["version"];
            latestAppURL = data["url"];
        } catch (_) {
            // optional
        }
    }

    function openCreate() {
        current = {
            name: "",
            country: "",
            maxUses: 1,
            bind: { ip: undefined, port: undefined },
            accessKey: undefined,
            tags: "",
        };
        createOpen = true;
    }

    function openEdit(device: any) {
        current = JSON.parse(JSON.stringify(device));
        if (Array.isArray(current.tags)) current.tags = current.tags.join(",");
        editOpen = true;
    }

    async function submitCreate() {
        if (typeof current.tags === "string") {
            current.tags = current.tags
                .split(",")
                .map((s: string) => s.trim())
                .filter(Boolean);
        }
        const response = await api.postDevices(current);
        await update();
        if (response.error) {
            toast.error(response.error.message, "Ошибка");
        } else {
            toast.success("Устройство создано", "Успех");
            createOpen = false;
        }
    }

    async function submitEdit() {
        if (typeof current.tags === "string") {
            current.tags = current.tags
                .split(",")
                .map((s: string) => s.trim())
                .filter(Boolean);
        }
        const response = await api.patchDevice(current["id"], current);
        await update();
        if (response.error) {
            toast.error(response.error.message, "Ошибка");
        } else {
            toast.success("Устройство обновлено", "Успех");
            editOpen = false;
        }
    }

    async function actionDevice(device: any, name: string) {
        const pendingId = toast.pending(
            "Создание действия...",
            "Ожидание: " + device.name,
        );
        try {
            const response = await api.postDeviceAction(device.id, name);
            toast.dismiss(pendingId);
            if (response.error) {
                toast.error(response.error.message, "Ошибка");
            } else {
                toast.success(
                    "Действие добавлено в очередь",
                    "Успех: " + device.name,
                );
                await update();
            }
        } catch (e: any) {
            toast.dismiss(pendingId);
            toast.error(String(e), "Ошибка");
        }
    }

    async function speedtestDevice(device: any) {
        const pendingId = toast.pending(
            "Идёт измерение скорости...",
            "Ожидание: " + device.name,
        );
        try {
            const response = await api.postDeviceSpeedtest(device.id);
            toast.dismiss(pendingId);
            if (response.error) {
                toast.error(response.error.message, "Ошибка: " + device.name);
            } else if (response.data) {
                toast.success(
                    formatSpeed(response.data.downloadSpeed) +
                        " (" +
                        response.data.serverName +
                        ")",
                    "Успех: " + device.name,
                );
                await update();
            }
        } catch (e: any) {
            toast.dismiss(pendingId);
            toast.error(String(e), "Ошибка");
        }
    }

    async function logsDevice(device: any) {
        current = device;
        const pendingId = toast.pending("Загрузка логов...", "Ожидание");
        try {
            // traffic-записи исключает сервер (без явного type), поэтому
            // limit тратится только на содержательные логи и они не
            // вытесняются. filter ниже — двойная подстраховка.
            const response = await api.getDeviceLogs(device.id, {
                limit: 100,
                order: "DESC",
            });
            toast.dismiss(pendingId);
            if (response.error) {
                toast.error(response.error.message, "Ошибка");
                return;
            }
            const filtered = response.data.filter(
                (log) => log["type"] !== "traffic",
            );
            for (const log of filtered) {
                if (log["type"] === "speedtest")
                    log["value"] = JSON.parse(log["value"]);
            }
            logs = filtered;
            logsOpen = true;
        } catch (e: any) {
            toast.dismiss(pendingId);
            toast.error(String(e), "Ошибка");
        }
    }

    async function loadConnections(device: any) {
        connsLoading = true;
        try {
            const [live, history] = await Promise.all([
                api.getDeviceConnections(device.id),
                api.getDeviceConnectionHistory(device.id, {
                    limit: 100,
                    order: "DESC",
                }),
            ]);
            if (live.error) {
                toast.error(live.error.message, "Ошибка");
            } else {
                // Больше соединений сверху.
                liveConns = (live.data ?? []).sort(
                    (a: any, b: any) => b.count - a.count,
                );
            }
            if (history.error) {
                toast.error(history.error.message, "Ошибка");
            } else {
                connHistory = history.data ?? [];
            }
        } catch (e: any) {
            toast.error(String(e), "Ошибка");
        } finally {
            connsLoading = false;
        }
    }

    async function connectionsDevice(device: any) {
        current = device;
        liveConns = [];
        connHistory = [];
        connsOpen = true;
        await loadConnections(device);
    }

    async function updateAppDevice(device: any) {
        await actionDevice({ ...device, name: device.name }, "update " + latestAppURL);
    }

    async function deleteDevice(device: any) {
        if (
            !confirm(
                'Удалить устройство "' + device.name + '"? Действие необратимо.',
            )
        )
            return;
        const response = await api.deleteDevice(device.id);
        await update();
        if (response.error) {
            toast.error(response.error.message, "Ошибка");
        } else {
            toast.success("Устройство удалено", "Успех");
        }
    }

    function copyConn(device: any) {
        const url =
            "socks5://" +
            $account.name +
            ":" +
            device.accessKey +
            "@" +
            device.bind.ip +
            ":" +
            device.bind.port;
        navigator.clipboard.writeText(url);
        toast.success("Скопировано в буфер", "Готово");
    }

    // Устройства, у которых прямо сейчас меняется пароль — чтобы не отправить
    // два запроса подряд и не сгенерировать пароль дважды.
    let rotatingIds = new Set<string>();

    async function rotateAccessKey(device: any) {
        if (rotatingIds.has(device.id)) return;
        if (
            !confirm(
                'Сменить пароль прокси "' +
                    device.name +
                    '"? Старый перестанет работать' +
                    (device.status === "online"
                        ? ", устройство переподключится"
                        : "") +
                    ".",
            )
        )
            return;

        rotatingIds.add(device.id);
        rotatingIds = rotatingIds;
        try {
            const response = await api.postDeviceAccessKey(device.id);
            if (response.error) {
                toast.error(response.error.message, "Ошибка: " + device.name);
                return;
            }
            toast.success(
                "Новый пароль: " + response.data.accessKey,
                "Успех: " + device.name,
            );
            await update();
        } catch (e: any) {
            toast.error(String(e), "Ошибка");
        } finally {
            rotatingIds.delete(device.id);
            rotatingIds = rotatingIds;
        }
    }

    // === Bulk speedtest ===
    const BULK_SPEEDTEST_CONCURRENCY = 3;
    let selectedIds = new Set<string>();
    let bulkRunning = false;
    let bulkCancel = false;
    let bulkDone = 0;
    let bulkTotal = 0;
    let bulkResults: {
        name: string;
        ok: boolean;
        speed?: string;
        server?: string;
        error?: string;
    }[] = [];

    $: selectedCount = selectedIds.size;
    $: hasOnlineSelection = devices.some(
        (d) => selectedIds.has(d.id) && d.status === "online",
    );

    function toggleSelect(id: string) {
        if (selectedIds.has(id)) selectedIds.delete(id);
        else selectedIds.add(id);
        selectedIds = selectedIds;
    }
    function selectAllOnline() {
        for (const d of devices) if (d.status === "online") selectedIds.add(d.id);
        selectedIds = selectedIds;
    }
    function clearSelection() {
        selectedIds.clear();
        selectedIds = selectedIds;
    }

    async function runBulkSpeedtest() {
        const targets = devices.filter(
            (d) => selectedIds.has(d.id) && d.status === "online",
        );
        if (targets.length === 0) return;
        bulkRunning = true;
        bulkCancel = false;
        bulkDone = 0;
        bulkTotal = targets.length;
        bulkResults = [];
        let idx = 0;
        async function worker() {
            while (true) {
                if (bulkCancel) return;
                const myIdx = idx++;
                if (myIdx >= targets.length) return;
                const device = targets[myIdx];
                try {
                    const resp = await api.postDeviceSpeedtest(device.id);
                    bulkResults = [
                        ...bulkResults,
                        {
                            name: device.name,
                            ok: !resp.error,
                            speed: resp.data?.downloadSpeed,
                            server: resp.data?.serverName,
                            error: resp.error?.message,
                        },
                    ];
                } catch (e) {
                    bulkResults = [
                        ...bulkResults,
                        { name: device.name, ok: false, error: String(e) },
                    ];
                }
                bulkDone += 1;
            }
        }
        const workers: Promise<void>[] = [];
        const N = Math.min(BULK_SPEEDTEST_CONCURRENCY, targets.length);
        for (let i = 0; i < N; i++) workers.push(worker());
        await Promise.all(workers);
        bulkRunning = false;
        try {
            await update();
        } catch (_) {}
    }

    let updateInterval: any;
    let updateReleaseInterval: any;
    onMount(async () => {
        await update();
        updateInterval = setInterval(async () => {
            try {
                await update();
            } catch (_) {}
        }, 3000);
        await updateRelease();
        updateReleaseInterval = setInterval(async () => {
            try {
                await updateRelease();
            } catch (_) {}
        }, 15000);
    });
    onDestroy(() => {
        clearInterval(updateInterval);
        clearInterval(updateReleaseInterval);
    });
</script>

<!-- ── Toolbar ── -->
<div
    class="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-[var(--border)]"
>
    <Button on:click={openCreate}>+ Создать</Button>

    <Input
        type="search"
        placeholder="Поиск..."
        bind:value={search}
        on:keydown={async (e) => {
            if (e.key === "Enter") {
                localStorage.setItem("search", search);
                await update();
            }
        }}
        class="!w-56"
    />

    <label
        class="inline-flex items-center gap-2 text-sm text-[var(--muted-foreground)] cursor-pointer select-none"
    >
        <Checkbox
            bind:checked={isShowOnlyOnline}
            on:change={async () => {
                localStorage.setItem(
                    "isShowOnlyOnline",
                    isShowOnlyOnline.toString(),
                );
                await update();
            }}
        />
        Только онлайн
    </label>

    <div class="flex-1"></div>

    {#if !bulkRunning}
        <Button
            variant="default"
            size="sm"
            disabled={!hasOnlineSelection}
            on:click={runBulkSpeedtest}
            title={"Замерить скорость у выбранных онлайн-устройств (по " +
                BULK_SPEEDTEST_CONCURRENCY +
                " параллельно)"}
        >
            Замерить ({selectedCount})
        </Button>
        {#if selectedCount > 0}
            <Button variant="ghost" size="sm" on:click={clearSelection}
                >Снять выбор</Button
            >
        {:else}
            <Button variant="ghost" size="sm" on:click={selectAllOnline}
                >Выбрать все онлайн</Button
            >
        {/if}
    {:else}
        <span class="text-sm text-[var(--muted-foreground)]"
            >Замер: {bulkDone}/{bulkTotal}</span
        >
        <Button
            variant="destructive"
            size="sm"
            on:click={() => (bulkCancel = true)}>Остановить</Button
        >
    {/if}
</div>

<!-- ── Bulk results ── -->
{#if bulkRunning || bulkResults.length > 0}
    <div
        class="mb-4 rounded-lg border border-[var(--border)] bg-[var(--card)] p-3 text-xs"
    >
        {#if bulkRunning}
            <div class="mb-2 text-[var(--foreground)]">
                Идёт замер: <strong>{bulkDone}/{bulkTotal}</strong>
                {#if bulkCancel}<span class="text-[var(--warning)] ml-1"
                        >(останавливаюсь...)</span
                    >{/if}
            </div>
        {:else}
            <div
                class="mb-2 flex items-center gap-2 text-[var(--foreground)]"
            >
                <span>Готово: <strong>{bulkResults.length}</strong></span>
                <Button
                    variant="ghost"
                    size="sm"
                    on:click={() => (bulkResults = [])}>Скрыть</Button
                >
            </div>
        {/if}
        <div class="flex flex-wrap" style="gap: 4px 12px;">
            {#each bulkResults as r}
                {#if r.ok}
                    <span class="text-[var(--success)]"
                        ><strong>{r.name}</strong>: {formatSpeed(r.speed)}
                        <span class="text-[var(--muted-foreground)]"
                            >({r.server})</span
                        ></span
                    >
                {:else}
                    <span class="text-[var(--destructive)]"
                        ><strong>{r.name}</strong>: {r.error || "ошибка"}</span
                    >
                {/if}
            {/each}
        </div>
    </div>
{/if}

<!-- ── Table ── -->
<div
    class="rounded-xl border border-[var(--border)] bg-[var(--card)]"
>
    <div class="overflow-x-auto">
        <table class="w-full text-sm">
            <thead
                class="text-[10px] uppercase tracking-wider text-[var(--muted-foreground)] bg-[var(--secondary)]/40"
            >
                <tr>
                    <th class="w-8 px-3 py-2"></th>
                    <th class="text-left px-3 py-2">Имя</th>
                    <th class="text-left px-3 py-2">Статус</th>
                    <th class="text-left px-3 py-2">Активность</th>
                    <th class="text-center px-3 py-2">Страна</th>
                    <th class="text-left px-3 py-2">Скорость</th>
                    <th class="text-left px-3 py-2">
                        <Tooltip
                            content={"Трафик через прокси (rx + tx), как видит сервер.\nНа SIM-карте телефона ~×2: байт идёт по моб. сети из интернета на телефон, потом по моб. сети в наш SSH-туннель."}
                        >
                            Трафик
                        </Tooltip>
                    </th>
                    <th class="text-left px-3 py-2">Макс.</th>
                    <th class="text-left px-3 py-2">Метки</th>
                    <th class="text-center px-3 py-2">Метрика</th>
                    <th class="text-left px-3 py-2">Подключение</th>
                </tr>
            </thead>
            <tbody>
                {#each devices as device (device.id)}
                    <tr
                        class="border-t border-[var(--border)] hover:bg-[var(--secondary)]/30 transition-colors"
                    >
                        <td class="px-3 py-2 align-middle text-center">
                            {#if device.status === "online"}
                                <Checkbox
                                    checked={selectedIds.has(device.id)}
                                    on:change={() => toggleSelect(device.id)}
                                    title="Выбрать для пакетного замера"
                                />
                            {/if}
                        </td>

                        <td class="px-3 py-2 align-middle">
                            <Dropdown align="start">
                                <button
                                    slot="trigger"
                                    type="button"
                                    class="text-[var(--foreground)] font-medium hover:text-[var(--primary)] cursor-pointer flex items-center gap-1 transition-colors"
                                >
                                    {device.name}
                                    <svg
                                        width="10"
                                        height="10"
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        class="opacity-50"
                                    >
                                        <path
                                            d="M3.204 5L8 10.481 12.796 5z"
                                        />
                                    </svg>
                                </button>

                                <DropdownItem on:click={() => openEdit(device)}
                                    >Редактировать</DropdownItem
                                >
                                <DropdownItem on:click={() => logsDevice(device)}
                                    >Логи</DropdownItem
                                >
                                <DropdownItem
                                    on:click={() => connectionsDevice(device)}
                                    >Клиенты</DropdownItem
                                >
                                <DropdownItem
                                    variant="destructive"
                                    on:click={() => deleteDevice(device)}
                                    >Удалить</DropdownItem
                                >

                                {#if device.status === "online"}
                                    <div
                                        class="border-t border-[var(--border)] my-1"
                                    ></div>
                                    <DropdownItem
                                        on:click={() =>
                                            actionDevice(device, "refresh")}
                                        >Переподключиться</DropdownItem
                                    >
                                    <DropdownItem
                                        on:click={() =>
                                            actionDevice(device, "switch_ip")}
                                        >Сменить IP</DropdownItem
                                    >
                                    <DropdownItem
                                        on:click={() => speedtestDevice(device)}
                                        >Измерить скорость</DropdownItem
                                    >
                                    {#if latestAppVersion !== "" && device["metrics"] && device["metrics"]["app"]["version"] !== latestAppVersion}
                                        <div
                                            class="border-t border-[var(--border)] my-1"
                                        ></div>
                                        <DropdownItem
                                            variant="warning"
                                            on:click={() =>
                                                updateAppDevice(device)}
                                            >Обновить приложение</DropdownItem
                                        >
                                    {/if}
                                {/if}
                            </Dropdown>
                        </td>

                        <td class="px-3 py-2 align-middle">
                            {#if device.status === "online"}
                                <Badge variant="success">Онлайн</Badge>
                            {:else}
                                <Badge variant="secondary">Оффлайн</Badge>
                            {/if}
                        </td>

                        <td class="px-3 py-2 align-middle">
                            {#if device["activity"]}
                                <div
                                    class={device.status === "online"
                                        ? "text-[var(--success)]"
                                        : "text-[var(--muted-foreground)]"}
                                >
                                    {activities[device["activity"]]}
                                </div>
                                {#if device["lastActivityAt"]}
                                    <div
                                        class="text-[10px] text-[var(--muted-foreground)] mt-0.5"
                                    >
                                        {new Date(
                                            device["lastActivityAt"],
                                        ).toLocaleString()}
                                    </div>
                                {/if}
                            {:else}
                                <span class="text-[var(--muted-foreground)]"
                                    >Нет</span
                                >
                            {/if}
                        </td>

                        <td
                            class="px-3 py-2 align-middle text-center text-[var(--muted-foreground)] uppercase text-xs"
                            >{device["country"]}</td
                        >

                        <td class="px-3 py-2 align-middle">
                            {device.lastSpeedtest
                                ? formatSpeed(
                                      JSON.parse(device.lastSpeedtest)
                                          .downloadSpeed,
                                  )
                                : "-"}
                        </td>

                        <td class="px-3 py-2 align-middle">
                            {#if device.traffic}
                                {#if (device.traffic.day.rx || 0) + (device.traffic.day.tx || 0) > 0 || (device.traffic.total.rx || 0) + (device.traffic.total.tx || 0) > 0}
                                    <Tooltip
                                        content={"За 24ч через прокси: " +
                                            formatBytes(
                                                (device.traffic.day.rx || 0) +
                                                    (device.traffic.day.tx ||
                                                        0),
                                            ) +
                                            "\nНа SIM ≈ " +
                                            formatBytes(
                                                ((device.traffic.day.rx ||
                                                    0) +
                                                    (device.traffic.day.tx ||
                                                        0)) *
                                                    2,
                                            ) +
                                            " (×2)"}
                                    >
                                        <div class="text-xs">
                                            <span class="font-semibold"
                                                >24ч:</span
                                            >
                                            {formatBytes(
                                                (device.traffic.day.rx || 0) +
                                                    (device.traffic.day.tx ||
                                                        0),
                                            )}
                                        </div>
                                    </Tooltip>
                                    <Tooltip
                                        content={"За 30д через прокси: " +
                                            formatBytes(
                                                (device.traffic.total.rx ||
                                                    0) +
                                                    (device.traffic.total.tx ||
                                                        0),
                                            ) +
                                            "\nНа SIM ≈ " +
                                            formatBytes(
                                                ((device.traffic.total.rx ||
                                                    0) +
                                                    (device.traffic.total.tx ||
                                                        0)) *
                                                    2,
                                            ) +
                                            " (×2)"}
                                    >
                                        <div
                                            class="text-xs text-[var(--muted-foreground)]"
                                        >
                                            30д: {formatBytes(
                                                (device.traffic.total.rx ||
                                                    0) +
                                                    (device.traffic.total.tx ||
                                                        0),
                                            )}
                                        </div>
                                    </Tooltip>
                                {:else}
                                    <span class="text-[var(--muted-foreground)]"
                                        >-</span
                                    >
                                {/if}
                            {:else}
                                <span class="text-[var(--muted-foreground)]"
                                    >-</span
                                >
                            {/if}
                        </td>

                        <td
                            class="px-3 py-2 align-middle text-[var(--muted-foreground)]"
                            >{device["maxUses"]}</td
                        >

                        <td class="px-3 py-2 align-middle">
                            {#if device["tags"] && device["tags"].filter((x) => x.length > 0).length > 0}
                                <div class="flex flex-wrap gap-1">
                                    {#each device["tags"] as tag}
                                        <Badge variant="default">{tag}</Badge>
                                    {/each}
                                </div>
                            {:else}
                                <span class="text-[var(--muted-foreground)]"
                                    >Нет</span
                                >
                            {/if}
                        </td>

                        <td class="px-3 py-2 align-middle">
                            {#if device["metrics"]}
                                <div
                                    class="flex flex-wrap items-center justify-center gap-2"
                                >
                                    <Network
                                        network={device["metrics"]["network"]}
                                    />
                                    <Battery
                                        battery={device["metrics"]["battery"]}
                                    />
                                </div>
                                <div
                                    class="text-[10px] text-center text-[var(--muted-foreground)] mt-1"
                                >
                                    {device["metrics"]["device"]["name"]} ({device[
                                        "metrics"
                                    ]["device"]["release"]},
                                    {#if latestAppVersion !== "" && device["metrics"]["app"]["version"] !== latestAppVersion}
                                        <span
                                            class="text-[var(--destructive)] font-bold"
                                            >v{device["metrics"]["app"][
                                                "version"
                                            ]}</span
                                        >)
                                    {:else}
                                        <span class="text-[var(--success)]"
                                            >v{device["metrics"]["app"][
                                                "version"
                                            ]}</span
                                        >)
                                    {/if}
                                </div>
                            {:else}
                                <span class="text-[var(--muted-foreground)]"
                                    >Нет</span
                                >
                            {/if}
                        </td>

                        <td
                            class="px-3 py-2 align-middle text-[var(--muted-foreground)] text-xs"
                        >
                            {#if device["bind"]}
                                <div class="font-mono">
                                    {$account.name}:{device["accessKey"]}@{device[
                                        "bind"
                                    ]["ip"]}:{device["bind"]["port"]}
                                </div>
                                <div class="mt-1.5 flex flex-wrap gap-1.5">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        class="!h-6 !px-2 !text-[10px]"
                                        on:click={() => copyConn(device)}
                                        >Копировать</Button
                                    >
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        class="!h-6 !px-2 !text-[10px]"
                                        disabled={rotatingIds.has(device.id)}
                                        title="Сгенерировать новый пароль прокси (24 символа). Старый перестанет работать, устройство переподключится."
                                        on:click={() => rotateAccessKey(device)}
                                        >{rotatingIds.has(device.id)
                                            ? "Меняю…"
                                            : "Сменить пароль"}</Button
                                    >
                                </div>
                            {:else}
                                Нет
                            {/if}
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    </div>
    {#if devices.length === 0}
        <div
            class="px-4 py-8 text-center text-[var(--muted-foreground)] text-sm"
        >
            Нет устройств
        </div>
    {/if}
</div>

<!-- ── Create Dialog ── -->
<Dialog
    bind:open={createOpen}
    title="Создание устройства"
    resizable
    storageKey="device-create"
    defaultWidth={560}
    defaultHeight={620}
>
    {#if current}
        <form
            class="flex flex-col gap-3"
            on:submit|preventDefault={submitCreate}
        >
            <div class="flex flex-col gap-1.5">
                <Label forId="cn">Имя</Label>
                <Input id="cn" required minlength={3} bind:value={current.name} />
            </div>
            <div class="flex flex-col gap-1.5">
                <Label forId="cc">Страна</Label>
                <Input
                    id="cc"
                    required
                    minlength={2}
                    bind:value={current.country}
                />
            </div>
            <div class="flex flex-col gap-1.5">
                <Label forId="cm">Макс. использований</Label>
                <Input
                    id="cm"
                    type="number"
                    required
                    bind:value={current.maxUses}
                />
            </div>
            <div class="flex flex-col gap-1.5">
                <Label forId="ct">Метки (через запятую)</Label>
                <Input
                    id="ct"
                    placeholder="Необязательно"
                    bind:value={current.tags}
                />
            </div>
            <div class="grid grid-cols-2 gap-3">
                <div class="flex flex-col gap-1.5">
                    <Label forId="cip">IP</Label>
                    <Input
                        id="cip"
                        placeholder="Необязательно"
                        bind:value={current.bind.ip}
                    />
                </div>
                <div class="flex flex-col gap-1.5">
                    <Label forId="cport">Порт</Label>
                    <Input
                        id="cport"
                        placeholder="Необязательно"
                        bind:value={current.bind.port}
                    />
                </div>
            </div>
            <div class="flex flex-col gap-1.5">
                <Label forId="ck">Ключ</Label>
                <Input
                    id="ck"
                    placeholder="Необязательно"
                    bind:value={current.accessKey}
                />
            </div>
            <div class="flex justify-end gap-2 pt-2">
                <Button
                    type="button"
                    variant="ghost"
                    on:click={() => (createOpen = false)}>Отмена</Button
                >
                <Button type="submit">Создать</Button>
            </div>
        </form>
    {/if}
</Dialog>

<!-- ── Edit Dialog ── -->
<Dialog
    bind:open={editOpen}
    title="Редактирование устройства"
    description={current ? current.id : ""}
    resizable
    storageKey="device-edit"
    defaultWidth={560}
    defaultHeight={640}
>
    {#if current}
        <form class="flex flex-col gap-3" on:submit|preventDefault={submitEdit}>
            <div class="flex flex-col gap-1.5">
                <Label forId="en">Имя</Label>
                <Input id="en" required minlength={3} bind:value={current.name} />
            </div>
            <div class="flex flex-col gap-1.5">
                <Label forId="ec">Страна</Label>
                <Input
                    id="ec"
                    required
                    minlength={2}
                    bind:value={current.country}
                />
            </div>
            <div class="flex flex-col gap-1.5">
                <Label forId="em">Макс. использований</Label>
                <Input
                    id="em"
                    type="number"
                    required
                    bind:value={current.maxUses}
                />
            </div>
            <div class="flex flex-col gap-1.5">
                <Label forId="et">Метки (через запятую)</Label>
                <Input
                    id="et"
                    placeholder="Необязательно"
                    bind:value={current.tags}
                />
            </div>
            <div class="grid grid-cols-2 gap-3">
                <div class="flex flex-col gap-1.5">
                    <Label forId="eip">IP</Label>
                    <Input
                        id="eip"
                        placeholder="Необязательно"
                        bind:value={current.bind.ip}
                    />
                </div>
                <div class="flex flex-col gap-1.5">
                    <Label forId="eport">Порт</Label>
                    <Input
                        id="eport"
                        placeholder="Необязательно"
                        bind:value={current.bind.port}
                    />
                </div>
            </div>
            <div class="flex flex-col gap-1.5">
                <Label forId="ek">Ключ</Label>
                <Input
                    id="ek"
                    placeholder="Необязательно"
                    bind:value={current.accessKey}
                />
            </div>
            <div class="flex justify-end gap-2 pt-2">
                <Button
                    type="button"
                    variant="ghost"
                    on:click={() => (editOpen = false)}>Отмена</Button
                >
                <Button type="submit">Сохранить</Button>
            </div>
        </form>
    {/if}
</Dialog>

<!-- ── Logs Dialog ── -->
<Dialog
    bind:open={logsOpen}
    title="Логи устройства"
    description={current ? current.name : ""}
    resizable
    storageKey="device-logs"
    defaultWidth={780}
    defaultHeight={560}
>
    <div
        class="rounded-lg border border-[var(--border)] divide-y divide-[var(--border)] text-xs"
    >
        {#each logs as log}
            <div
                class="px-3 py-2 flex items-start gap-3 hover:bg-[var(--secondary)]/40"
            >
                <div
                    class="text-[var(--muted-foreground)] whitespace-nowrap shrink-0"
                >
                    {new Date(log["createdAt"]).toLocaleString()}
                </div>
                <div class="flex-1">
                    {#if log["type"] === "ip"}
                        Подключение с IP: <span class="font-semibold"
                            >{log["value"]}</span
                        >
                    {:else if log["type"] === "speedtest"}
                        Скорость: {log["value"]["ping"]}ms / {formatSpeed(
                            log["value"]["downloadSpeed"],
                        )}
                        <span class="text-[var(--muted-foreground)]"
                            >({log["value"]["serverName"]})</span
                        >
                    {:else}
                        <span class="text-[var(--muted-foreground)]"
                            >{log["type"]}:</span
                        >
                        {log["value"]}
                    {/if}
                </div>
            </div>
        {:else}
            <div class="px-3 py-6 text-center text-[var(--muted-foreground)]">
                Нет логов
            </div>
        {/each}
    </div>
</Dialog>

<!-- ── Connections Dialog ── -->
<Dialog
    bind:open={connsOpen}
    title="Клиенты устройства"
    description={current ? current.name : ""}
    resizable
    storageKey="device-connections"
    defaultWidth={720}
    defaultHeight={560}
>
    <div class="flex items-center justify-between mb-3">
        <div class="text-sm font-medium text-[var(--foreground)]">
            Подключены сейчас
        </div>
        <Button
            variant="outline"
            size="sm"
            on:click={() => current && loadConnections(current)}
            disabled={connsLoading}
        >
            {connsLoading ? "Обновление…" : "Обновить"}
        </Button>
    </div>

    <div
        class="rounded-lg border border-[var(--border)] divide-y divide-[var(--border)] text-xs mb-5"
    >
        {#each liveConns as c}
            <div
                class="px-3 py-2 flex items-center gap-3 hover:bg-[var(--secondary)]/40"
            >
                <span class="font-mono font-semibold text-[var(--foreground)]"
                    >{c.ip}</span
                >
                <span
                    class="ml-auto text-[var(--muted-foreground)] whitespace-nowrap"
                >
                    {c.count} соед. · c {new Date(
                        c.firstSeen,
                    ).toLocaleTimeString()}
                </span>
            </div>
        {:else}
            <div class="px-3 py-6 text-center text-[var(--muted-foreground)]">
                Сейчас никто не подключён
            </div>
        {/each}
    </div>

    <div class="text-sm font-medium text-[var(--foreground)] mb-2">
        История подключений
    </div>
    <div
        class="rounded-lg border border-[var(--border)] divide-y divide-[var(--border)] text-xs"
    >
        {#each connHistory as ev}
            <div
                class="px-3 py-2 flex items-center gap-3 hover:bg-[var(--secondary)]/40"
            >
                <span
                    class="text-[var(--muted-foreground)] whitespace-nowrap shrink-0"
                >
                    {new Date(ev.createdAt).toLocaleString()}
                </span>
                <span class="font-mono text-[var(--foreground)]">{ev.ip}</span>
            </div>
        {:else}
            <div class="px-3 py-6 text-center text-[var(--muted-foreground)]">
                История пуста
            </div>
        {/each}
    </div>
</Dialog>
